import { useEffect, useState } from "react";
import api from "../api/client";
import { toast } from "sonner";
import { Download, Mail, Printer, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import ClientReportsCard from "./ClientReportsCard";
import logger from "../lib/logger";

const PERIODS = [
  {
    id: "weekly",
    label: "Weekly",
    description: "Last N weeks, broken down by week starting Monday.",
    defaultCount: 4,
    countOptions: [1, 2, 4, 8, 12],
    countLabel: (n) => `${n} week${n > 1 ? "s" : ""}`,
  },
  {
    id: "monthly",
    label: "Monthly",
    description: "Last N calendar months, broken down by month.",
    defaultCount: 3,
    countOptions: [1, 2, 3, 6, 12],
    countLabel: (n) => `${n} month${n > 1 ? "s" : ""}`,
  },
  {
    id: "yearly",
    label: "Yearly",
    description: "Last N calendar years, broken down by year.",
    defaultCount: 1,
    countOptions: [1, 2, 3, 5],
    countLabel: (n) => `${n} year${n > 1 ? "s" : ""}`,
  },
];

export default function ReportsTab() {
  const [activePeriod, setActivePeriod] = useState("weekly");
  const [counts, setCounts] = useState({ weekly: 4, monthly: 3, yearly: 1 });
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sendingDaily, setSendingDaily] = useState(false);

  useEffect(() => {
    api.get("/reports/clients").then(({ data }) => setClients(data || [])).catch(() => {});
  }, []);

  const current = PERIODS.find((p) => p.id === activePeriod);

  const downloadWorkbook = async () => {
    setDownloading(true);
    try {
      const params = { period: activePeriod, count: counts[activePeriod] };
      if (clientFilter) params.client = clientFilter;
      const res = await api.get("/reports/workbook.xlsx", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const suffix = clientFilter ? `_${clientFilter.replace(/\s+/g, "_")}` : "";
      a.href = url;
      a.download = `avidity_${activePeriod}_report${suffix}_${counts[activePeriod]}${activePeriod[0]}_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${current.label} workbook downloaded`);
    } catch (err) {
      logger.error(err);
      toast.error("Failed to download workbook");
    } finally {
      setDownloading(false);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const params = { period: activePeriod, count: counts[activePeriod] };
      if (clientFilter) params.client = clientFilter;
      const res = await api.get("/reports/workbook.pdf", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const suffix = clientFilter ? `_${clientFilter.replace(/\s+/g, "_")}` : "";
      a.href = url;
      a.download = `avidity_${activePeriod}_report${suffix}_${counts[activePeriod]}${activePeriod[0]}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${current.label} PDF downloaded`);
    } catch (err) {
      logger.error(err);
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const sendDailyNow = async () => {
    setSendingDaily(true);
    try {
      const { data } = await api.post("/reports/daily-report/send-now");
      if (data?.sent) {
        toast.success(`Daily report emailed to ${data.recipients?.join(", ")}`);
      } else if (data?.reason === "no_recipients") {
        toast.error("No DAILY_REPORT_RECIPIENTS configured in backend .env");
      } else {
        toast.message("Email queued (placeholder mode — set RESEND_API_KEY to enable real sending)");
      }
    } catch (err) {
      logger.error(err);
      toast.error(err?.response?.data?.detail || "Failed to send daily report");
    } finally {
      setSendingDaily(false);
    }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="reports-tab">
      {/* Top: daily auto-email status & manual trigger */}
      <section
        data-testid="daily-email-card"
        className="border border-gray-300 bg-white p-5 rounded-sm"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-gray-500">Automated email</div>
            <h3 className="text-xl font-black tracking-tight mt-1">Daily report — administrator only</h3>
            <p className="text-sm text-gray-600 mt-2">
              A 1-day operations workbook is emailed automatically to the administrator every
              morning at 08:00 UTC. Clients never receive this email. Weekly, monthly, and
              yearly reports are available on demand below.
            </p>
          </div>
          <button
            onClick={sendDailyNow}
            disabled={sendingDaily}
            data-testid="send-daily-now-button"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white font-bold tracking-wide hover:bg-[#002277] disabled:opacity-50 rounded-sm transition"
          >
            {sendingDaily ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" /> Send daily report now
              </>
            )}
          </button>
        </div>
      </section>

      {/* Per-client weekly report dispatch */}
      <ClientReportsCard />

      {/* Period sub-tabs */}
      <section className="border border-gray-300 bg-white rounded-sm">
        <div className="flex gap-px bg-gray-200 border-b border-gray-300">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setActivePeriod(p.id)}
              data-testid={`reports-period-${p.id}`}
              className={`flex-1 px-6 py-3 text-sm font-semibold transition ${
                activePeriod === p.id
                  ? "bg-white text-[#002FA7]"
                  : "bg-gray-100 text-gray-600 hover:bg-white"
              }`}
            >
              {p.label} report
            </button>
          ))}
        </div>

        <div className="p-6 space-y-5">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="w-6 h-6 text-[#002FA7] mt-0.5" />
            <div>
              <div className="text-lg font-bold tracking-tight">{current.label} operations report</div>
              <div className="text-sm text-gray-600 mt-1">{current.description}</div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="reports-count-select"
                className="block text-xs uppercase tracking-widest text-gray-500 mb-2"
              >
                Range
              </label>
              <select
                id="reports-count-select"
                data-testid="reports-count-select"
                value={counts[activePeriod]}
                onChange={(e) =>
                  setCounts((prev) => ({ ...prev, [activePeriod]: Number(e.target.value) }))
                }
                className="w-full border border-gray-300 px-3 py-2 rounded-sm bg-white"
              >
                {current.countOptions.map((n) => (
                  <option key={n} value={n}>
                    {current.countLabel(n)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="reports-client-select"
                className="block text-xs uppercase tracking-widest text-gray-500 mb-2"
              >
                Filter (optional)
              </label>
              <select
                id="reports-client-select"
                data-testid="reports-client-select"
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 rounded-sm bg-white"
              >
                <option value="">All clients</option>
                {clients.map((c) => (
                  <option key={c.client_name} value={c.client_name}>
                    {c.client_name} ({c.site_count} site{c.site_count > 1 ? "s" : ""})
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-gray-500 mt-1">
                Used to scope a downloaded workbook to one client. Automated emails ignore this and
                go to the administrator only.
              </p>
            </div>
          </div>

          <div className="flex gap-3 flex-wrap pt-2 border-t border-gray-200">
            <button
              onClick={downloadWorkbook}
              disabled={downloading}
              data-testid="download-workbook-button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white font-bold tracking-wide hover:bg-[#002277] disabled:opacity-50 rounded-sm transition"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" /> Download {current.label} .xlsx
                </>
              )}
            </button>
            <button
              onClick={downloadPdf}
              disabled={downloadingPdf}
              data-testid="download-pdf-button"
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#002FA7] text-[#002FA7] font-bold tracking-wide hover:bg-[#002FA7] hover:text-white disabled:opacity-50 rounded-sm transition"
            >
              {downloadingPdf ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" /> Download {current.label} .pdf
                </>
              )}
            </button>
            <button
              onClick={() => window.print()}
              data-testid="print-page-button"
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 font-bold tracking-wide hover:bg-gray-100 rounded-sm transition"
            >
              <Printer className="w-4 h-4" /> Print this page
            </button>
          </div>
        </div>
      </section>

      <p className="text-xs text-gray-500 leading-relaxed">
        Choose <strong>.xlsx</strong> for the raw data and embedded charts (open in Excel/Sheets/Numbers),
        or <strong>.pdf</strong> for a branded, print-ready handout. Downloaded files include
        Overview, Compliance, {current.label} Services, and Workplaces Cleaned.
      </p>
    </div>
  );
}
